import React from 'react';

// Material-UI
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import { amber, blueGrey, green, red } from '@mui/material/colors';
import Drawer from '@mui/material/Drawer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import PanToolIcon from '@mui/icons-material/PanTool';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import CancelledSignup from './CancelledSignup';
import MyRequestsHelp from './MyRequestsHelp';
import MyRequestsHelpDialog from './MyRequestsHelpDialog';
import PastRequest from './PastRequest';
import RequestEdit from './RequestEdit';
import WaiverDialog from '../WaiverDialog';

import {
  HTTPHeaders,
  MemberWaiverStatus,
} from '../../interfaces';

import {
  HouseholdSignups,
  SignupHousehold
} from '../../models';

import {
  API_URL,
  NULL_ID,
  SignupStatusID,
  WaiverStatus
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';
// Date formatting
import { useDates } from '../../hooks/UseDates';
// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';
// Signup related dates hooks
import { useSignupDates } from '../../hooks/UseSignupDates';

// Redux
import { useAppSelector } from '../../hooks/UseReduxState';
import { getGuestRequestCancelled, getGuestRequestSaved, getMemberRequestCancelled, getMemberRequestSaved, getNotSignedUpAdded, getRequestCancelled } from '../../../redux/requestSlice'


const NODEID_CANCELLED = 'cancelled';
const NODEID_CURRENT = 'current';
const NODEID_FUTURE = 'future';
const NODEID_PAST = 'past';

enum MyRequestsCategory {
  Edit = 1,
  Past = 2,
  Cancelled = 3,
  Summary = 99
}

const MyRequests: React.FC = () => {

  const { getAccessToken, memberID } = useAuth0User();
  const { isMobile } = useResponsive();
  const { getRequestYearEndDate, getRequestYearStartDate } = useSignupDates();
  const { dateStringFormatted } = useDates();

  const [cancelledDrawer, setCancelledDrawer] = React.useState<boolean>(false);
  const [editDrawer, setEditDrawer] = React.useState<boolean>(false);
  const [expandedNodes, setExpandedNodes] = React.useState<Array<string>>([]);
  const [helpOpen, setHelpOpen] = React.useState<boolean>(false);
  const [lodgeUseMode, setMyRequestsCategory] = React.useState(MyRequestsCategory.Summary);
  const [memberWaivers, setMemberWaivers] = React.useState<Array<MemberWaiverStatus>>([]);
  const [pastDrawer, setPastDrawer] = React.useState<boolean>(false);
  const [showWorking, setShowWorking] = React.useState<boolean>(false);
  const [signupID, setSignupID] = React.useState(NULL_ID);
  const [signups, setSignups] = React.useState<HouseholdSignups>(new HouseholdSignups());
  const [waiverDialogOpen, setWaiverDialogOpen] = React.useState<boolean>(false);

  // Redux
  const guestCancelled: boolean = useAppSelector(getGuestRequestCancelled);
  const guestSaved: boolean = useAppSelector(getGuestRequestSaved);
  const memberCancelled: boolean = useAppSelector(getMemberRequestCancelled);
  const memberSaved: boolean = useAppSelector(getMemberRequestSaved);
  const notSignedUpAdded: boolean = useAppSelector(getNotSignedUpAdded);
  const requestCancelled: boolean = useAppSelector(getRequestCancelled);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      padding: theme.spacing(1, 1, 1, 1)
    },
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    drawer: {
      padding: theme.spacing(1, 1, 1, 1),
      flexGrow: 1,
      height: '100%'
    },
    heading: {
      padding: theme.spacing(1, 0, 1, 0)
    },
    mixed: {
      color: green[500]
    },
    pending: {
      color: blueGrey[500]
    },
    requestsContainer: {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    waiting: {
      padding: theme.spacing(2, 0, 2, 0)

    },
    waitlist: {
      color: amber[500]
    },
  };

  // #endregion

  // #region Methods

  /*
  * Get household waivers needed for any member in a current or future request
  */
  const fetchMemberWaivers = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const id = memberID();
    const url = new URL(`${API_URL}/v1/members/memberrequestwaivers/${id}`);

    return await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to get member waiver status');
        }
        return response.json()
      })
      .then((ws: Array<MemberWaiverStatus>) => {
        setMemberWaivers(ws);
      })
      .catch(err => {
        throw new Error('Unable to get member waiver status');
      });
  }

  /*
   * Fetch the household requests
   */
  const fetchSignups = async () => {

    setShowWorking(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // Always show current date forward
    const params = new URLSearchParams();

    // Filter to current member/user and request year
    params.append(`memberID`, `${memberID()}`);
    params.append(`startDate`, getRequestYearStartDate());
    params.append(`endDate`, getRequestYearEndDate());

    const url = new URL(`${API_URL}/v1/signups/household`);
    url.search = params.toString();

    await fetch(url.toString(),
      {
        method: 'GET'
        , cache: 'no-cache'
        , headers: headers
        , mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setShowWorking(false);
          throw new Error('Household requests not retrieved');
        }
        return response.json()
      })
      .then((s: HouseholdSignups) => {
        setSignups(s);
        const exp: Array<string> = [];
        if (s.currentSignups.length > 0) {
          exp.push(NODEID_CURRENT);
        }
        if (s.futureSignups.length > 0) {
          exp.push(NODEID_FUTURE);
        }
        setExpandedNodes(exp);
        setShowWorking(false);
      }
      )
      .catch(err => {
        setShowWorking(false);
        throw new Error('Household requests not retrieved');
      });
  }

  /*
  * Render lable for treeview node
  */
  const renderLabel = (signup: SignupHousehold) => {
    const arrival: string = dateStringFormatted(signup.minArrivalDate, 'M/d/yy');
    const departure: string = dateStringFormatted(signup.maxDepartureDate, 'M/d/yy');

    if (signup.priorityRequest) {
      return <><Tooltip title={signup.referenceNumber}><Typography component="span">{arrival} - {departure}</Typography></Tooltip><Tooltip title="Priority"><Typography variant="caption" component="span"> (P)</Typography></Tooltip></>;
    } else if (signup.postDeadline) {
      return <><Tooltip title={signup.referenceNumber}><Typography component="span">{arrival} - {departure}</Typography></Tooltip><Tooltip title="Post Deadline"><Typography variant="caption" component="span"> (PD)</Typography></Tooltip></>;
    } else {
      return <Tooltip title={signup.referenceNumber}><Typography>{arrival} - {departure}</Typography></Tooltip>;
    }
  }

  /*
   * Render status icon for request
   */
  const renderStatusIcon = (status: number) => {
    switch (status) {
      case SignupStatusID.Confirmed:
        return <Tooltip title="Confirmed">
          <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
        </Tooltip>;
      case SignupStatusID.Denied:
        return <Tooltip title="Denied">
          <BlockIcon fontSize="medium" sx={styles.deined} />
        </Tooltip>;
      case SignupStatusID.Mixed:
        return <Tooltip title="Mixed">
          <AutoAwesomeIcon fontSize="medium" sx={styles.deined} />
        </Tooltip>;
      case SignupStatusID.Requested:
        return <Tooltip title="Pending">
          <PendingIcon fontSize="medium" sx={styles.pending} />
        </Tooltip>;
      case SignupStatusID.Waitlist:
        return <Tooltip title="Waitlist">
          <PanToolIcon fontSize="medium" sx={styles.waitlist} />
        </Tooltip>;
      default:
    }
  }

  // #endregion

  // #region Event handlers

  /*
 * Handle closing of cancelled requests 
 */
  const handleCancelClose = () => {
    setCancelledDrawer(false);
  };

  /*
* Handle closing of request edit drawer
*/
  const handleEditClose = () => {
    setEditDrawer(false);
  };

  /*
 * Handler for help dialog window
 */
  const handleHelpClose = () => {
    setHelpOpen(false);
  }

  /*
   * Handle toggle of treeview node
   */
  const handleNodeToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
    setExpandedNodes(nodeIds);
  };

  /*
   * Handles action after selection of a signup
   */
  const handleNodeSelected = (signupID: number, mode: MyRequestsCategory) => () => {

    setSignupID(signupID);

    if (isMobile) {
      switch (mode) {
        case MyRequestsCategory.Cancelled:
          setCancelledDrawer(true);
          break;
        case MyRequestsCategory.Edit:
          setEditDrawer(true);
          break;
        case MyRequestsCategory.Past:
          setPastDrawer(true);
          break;
        default:
      }
    }
    setMyRequestsCategory(mode);
  }

  /*
* Handle closing of past request drawer
*/
  const handlePastClose = () => {
    setPastDrawer(false);
  };

  /*
* Handler for waiver dialog window
*/
  const handleWaiverDialogClose = () => {
    setWaiverDialogOpen(false);
  }

  React.useEffect(() => {
    fetchMemberWaivers();
    fetchSignups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (guestCancelled) {
      fetchSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestCancelled]);

  React.useEffect(() => {
    if (guestSaved) {
      fetchSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestSaved]);

  React.useEffect(() => {
    if (memberCancelled) {
      fetchSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberCancelled]);

  React.useEffect(() => {
    if (memberSaved) {
      fetchSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSaved]);

  React.useEffect(() => {
    if (notSignedUpAdded) {
      fetchSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notSignedUpAdded]);

  React.useEffect(() => {
    if (requestCancelled) {
      setMyRequestsCategory(MyRequestsCategory.Summary);
      fetchSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCancelled]);

  React.useEffect(() => {
    if ((memberWaivers.length > 0) && memberWaivers.some(m => m.waiverStatusID === WaiverStatus.Pending)) {
      setWaiverDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(memberWaivers)]);

  // #endregion

  return (
    <>
      <Grid container spacing={2} sx={styles.root}>
        <Grid sx={!isMobile ? styles.requestsContainer : {}}>
          <Grid container alignItems="center" sx={styles.heading}>
            <Grid>
              <Typography variant="h6">Requests</Typography>
            </Grid>
            <Grid>
              <Tooltip title="Lodge Use Requests Help">
                <IconButton
                  onClick={() => setHelpOpen(true)}
                  size="large"
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container>
            <SimpleTreeView
              expandedItems={expandedNodes}
              onExpandedItemsChange={handleNodeToggle}
              slots={{
                collapseIcon: ExpandMoreIcon,
                expandIcon: ChevronRightIcon
              }}
            >
              <TreeItem
                itemId={NODEID_CURRENT}
                label={'Current (' + (signups.currentSignups && signups.currentSignups.length > 0 ? signups.currentSignups.length.toString() : 'None') + ')'}
              >
                {(signups.currentSignups && signups.currentSignups.length > 0) &&
                  signups.currentSignups.map((signup: SignupHousehold) => (
                    <TreeItem
                      key={signup.signupID}
                      itemId={signup.signupID.toString()}
                      label={renderLabel(signup)}
                      onClick={handleNodeSelected(signup.signupID, MyRequestsCategory.Edit)}
                    />
                  ))
                }
              </TreeItem>
              <TreeItem
                itemId={NODEID_FUTURE}
                label={'Future (' + (signups.futureSignups && signups.futureSignups.length > 0 ? signups.futureSignups.length.toString() : 'None') + ')'}
              >
                {(signups.futureSignups && signups.futureSignups.length > 0) &&
                  signups.futureSignups.map((signup: SignupHousehold) => (
                    <TreeItem
                      icon={renderStatusIcon(signup.signupStatusID)}
                      key={signup.signupID}
                      itemId={signup.signupID.toString()}
                      label={renderLabel(signup)}
                      onClick={handleNodeSelected(signup.signupID, MyRequestsCategory.Edit)}
                    />
                  ))
                }
              </TreeItem>
              <TreeItem
                itemId={NODEID_PAST}
                label={'Past (' + (signups.pastSignups && signups.pastSignups.length > 0 ? signups.pastSignups.length.toString() : 'None') + ')'}
              >
                {(signups.pastSignups && signups.pastSignups.length > 0) &&
                  signups.pastSignups.map((signup: SignupHousehold) => (
                    <TreeItem
                      key={signup.signupID}
                      itemId={signup.signupID.toString()}
                      label={renderLabel(signup)}
                      onClick={handleNodeSelected(signup.signupID, MyRequestsCategory.Past)}
                    />
                  ))
                }
              </TreeItem>
              <TreeItem
                itemId={NODEID_CANCELLED}
                label={'Cancellations (' + (signups.cancelledSignups && signups.cancelledSignups.length > 0 ? signups.cancelledSignups.length.toString() : 'None') + ')'}
              >
                {(signups.cancelledSignups && signups.cancelledSignups.length > 0) &&
                  signups.cancelledSignups.map((signup: SignupHousehold) => (
                    <TreeItem
                      key={signup.signupID}
                      itemId={signup.signupID.toString()}
                      label={renderLabel(signup)}
                      onClick={handleNodeSelected(signup.signupID, MyRequestsCategory.Cancelled)}
                    />
                  ))
                }
              </TreeItem>
            </SimpleTreeView>
          </Grid>
          {showWorking &&
            <Grid container justifyContent="center" sx={styles.waiting}>
              <CircularProgress />
            </Grid>
          }
        </Grid>
        {!isMobile &&
          <Grid>
            {(lodgeUseMode === MyRequestsCategory.Cancelled) &&
              <CancelledSignup
                closeDrawer={handleCancelClose}
                signupID={signupID}
              />
            }
            {lodgeUseMode === MyRequestsCategory.Edit &&
              <RequestEdit
                closeDrawer={handleEditClose}
                signupID={signupID}
              />
            }
            {lodgeUseMode === MyRequestsCategory.Past &&
              <PastRequest
                closeDrawer={handlePastClose}
                signupID={signupID}
              />
            }
            {(lodgeUseMode === MyRequestsCategory.Summary) &&
              <MyRequestsHelp />
            }
          </Grid>
        }
      </Grid>
      <Drawer
        variant="temporary"
        anchor={'bottom'}
        open={cancelledDrawer}
        onClose={handleCancelClose}
        PaperProps={{ style: { height: '100%' } }}
      >
        <div style={styles.drawer}>
          {lodgeUseMode === MyRequestsCategory.Cancelled &&
            <CancelledSignup
              closeDrawer={handleCancelClose}
              signupID={signupID}
            />}
        </div>
      </Drawer>
      <Drawer
        variant="temporary"
        anchor={'bottom'}
        open={editDrawer}
        onClose={handleEditClose}
        PaperProps={{ style: { height: '100%' } }}
      >
        <div style={styles.drawer}>
          <Grid size={12}>
            {(lodgeUseMode === MyRequestsCategory.Cancelled) &&
              <CancelledSignup
                closeDrawer={handleCancelClose}
                signupID={signupID}
              />
            }
            {lodgeUseMode === MyRequestsCategory.Edit &&
              <RequestEdit
                closeDrawer={handleEditClose}
                signupID={signupID}
              />
            }
            {lodgeUseMode === MyRequestsCategory.Past &&
              <PastRequest
                closeDrawer={handlePastClose}
                signupID={signupID}
              />
            }
            {(lodgeUseMode === MyRequestsCategory.Summary) &&
              <MyRequestsHelp />
            }
          </Grid>
        </div>
      </Drawer>
      <Drawer
        variant="temporary"
        anchor={'bottom'}
        open={pastDrawer}
        onClose={handlePastClose}
        PaperProps={{ style: { height: '100%' } }}
      >
        <div style={styles.drawer}>
          {lodgeUseMode === MyRequestsCategory.Past &&
            <PastRequest
              closeDrawer={handlePastClose}
              signupID={signupID}
            />
          }
        </div>
      </Drawer>
      <MyRequestsHelpDialog
        show={helpOpen}
        handleClose={handleHelpClose}
      />
      <WaiverDialog
        handleClose={handleWaiverDialogClose}
        memberWaivers={memberWaivers}
        show={waiverDialogOpen}
        handleWaiverSigned={fetchMemberWaivers}
      />
    </>
  );

}

export default MyRequests;